import { useState } from "react";
import {
  ActionIcon,
  Button,
  FileInput,
  Group,
  Image,
  NumberInput,
  Paper,
  Radio,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconTrash, IconUpload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { api } from "../../api/client";
import type { LabelBackground, LabelType } from "../../api/types";

interface Props {
  labelType: LabelType;
  backgrounds: LabelBackground[];
  defaultBackgroundId: string | null;
  onRefresh: () => Promise<void>;
}

export function BackgroundLibrary({
  labelType,
  backgrounds,
  defaultBackgroundId,
  onRefresh,
}: Props) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  async function upload() {
    if (!file) return;
    setBusy(true);
    try {
      await api.uploadLabelBackground(labelType, file, { name: file.name });
      setFile(null);
      await onRefresh();
      notifications.show({ color: "green", message: t("labelEditor.bgUploaded") });
    } catch {
      notifications.show({ color: "red", message: t("labelEditor.bgUploadFailed") });
    } finally {
      setBusy(false);
    }
  }

  async function patch(bgId: string, fields: Parameters<typeof api.updateLabelBackground>[2]) {
    try {
      await api.updateLabelBackground(labelType, bgId, fields);
      await onRefresh();
    } catch {
      notifications.show({ color: "red", message: t("labelEditor.bgUpdateFailed") });
    }
  }

  async function makeDefault(bgId: string) {
    try {
      await api.updateLabelTemplate(labelType, { default_background_id: bgId });
      await onRefresh();
    } catch {
      notifications.show({ color: "red", message: t("labelEditor.bgUpdateFailed") });
    }
  }

  async function remove(bgId: string) {
    if (!window.confirm(t("labelEditor.bgDeleteConfirm"))) return;
    try {
      await api.deleteLabelBackground(labelType, bgId);
      await onRefresh();
    } catch {
      notifications.show({ color: "red", message: t("labelEditor.bgDeleteFailed") });
    }
  }

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Title order={5}>{t("labelEditor.backgrounds")}</Title>
        <Group align="end" gap="xs">
          <FileInput
            placeholder={t("labelEditor.bgChooseFile")}
            accept="image/png,image/jpeg,image/bmp"
            value={file}
            onChange={setFile}
            leftSection={<IconUpload size={14} />}
            style={{ flex: 1 }}
          />
          <Button onClick={upload} disabled={!file || busy} loading={busy}>
            {t("labelEditor.bgUpload")}
          </Button>
        </Group>

        {backgrounds.length === 0 ? (
          <Text size="sm" c="dimmed">{t("labelEditor.bgEmpty")}</Text>
        ) : (
          backgrounds.map((bg) => (
            <Group key={bg.id} align="start" wrap="nowrap"
                   style={{ border: "1px solid #e5e5e5", borderRadius: 4, padding: 8 }}>
              <Image
                src={api.labelBackgroundUrl(labelType, bg.id)}
                w={80} h={60} fit="contain" alt=""
              />
              <Stack gap={4} style={{ flex: 1 }}>
                <TextInput
                  value={bg.name}
                  onChange={(e) => patch(bg.id, { name: e.currentTarget.value })}
                />
                <Group gap="xs">
                  <Select
                    size="xs"
                    data={["contain", "cover", "stretch"]}
                    value={bg.fit}
                    onChange={(v) => v && patch(bg.id, { fit: v })}
                    allowDeselect={false}
                  />
                  <NumberInput
                    size="xs"
                    label="threshold"
                    value={bg.threshold}
                    min={0} max={255}
                    onChange={(v) =>
                      typeof v === "number" && patch(bg.id, { threshold: v })}
                    w={120}
                  />
                </Group>
                <Group gap="xs">
                  <Radio
                    label={t("labelEditor.bgDefault")}
                    checked={bg.id === defaultBackgroundId}
                    onChange={() => makeDefault(bg.id)}
                  />
                  <ActionIcon variant="subtle" color="red" onClick={() => remove(bg.id)}>
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              </Stack>
            </Group>
          ))
        )}
      </Stack>
    </Paper>
  );
}
